<template>
  <div>
    <div class="dx-fieldset">
      <dx-data-grid id="dxdatagrid"
                    ref="dxdatagrid"
                    key-expr="Code"
                    :data-source="dashdata.list"
                    :show-borders="true"
                    :word-wrap-enabled="true"
                    :show-column-lines="false"
                    :show-row-lines="true"
                    :focused-row-enabled="false"
                    :onCellPrepared="onCellPrepared"
                    :allow-column-reordering="false"
                    :row-alternation-enabled="false"
                    :column-hiding-enabled="true"
                    :onRowClick="onRowClick"
                    :key="componentKey"
                    :onRowCollapsed="onGridRowCollapsed"
                    :onRowExpanded="onGridRowExpanded"
                    :onContentReady="manipulateGridDom"
                    :onAdaptiveDetailRowPreparing="onGridAdaptiveDetailRowPreparing"
                    @toolbar-preparing="onToolbarPreparing($event)">
        <dx-grouping :autoExpandAll="false">
          <dx-texts 
            :groupContinuedMessage="$t('WebReport.DataGridContinuedMessage')" 
            :groupContinuesMessage="$t('WebReport.DataGridContinuesMessage')" />
        </dx-grouping>

        <dx-paging :enabled="false" />
        <dx-sorting mode="none"/>

        <dx-export
          :enabled="false"
          :file-name="getExcelFilename()"
          :customize-excel-cell="customizeExcelCell"/>

        <dx-column  :data-field="getGroupColName()"
                    :customizeText="getGroupName"
                    :group-index="0"
                    :sortingMethod="()=>{return 0;}"
                    groupCellTemplate="summaryCellTemplate"/>

        <dx-column  v-if="compareModeEnabled && groupModeEnabled"
                    data-field="GroupCode"
                    :group-index="1"
                    :sortingMethod="()=>{return 0;}"
                    groupCellTemplate="summaryCellTemplate"/>

        <dx-column  :caption="getClusterCaption()">
          <dx-column  data-field="Name"
                      :caption="getSubClusterCaption()" 
                      :width="260"/>
        </dx-column>

        <dx-column v-if="compareModeEnabled && !groupModeEnabled" :caption="$t('Webportal.Dashboard.Grid.CompareCaption')">
          <dx-column  data-field="GroupCode"
                      :caption="activeCompareLabel"
                      cellTemplate="summaryCellTemplate"
                      :width="100"/>
        </dx-column>

        <template v-for="(wave, index) in this.waves">
          <dx-column :caption="wave" :key="`${index}_wave`" :width="150">
            <dx-column  :data-field="getWaveResultField(index+1)"
                        :visible="true"
                        :caption="$t('Webportal.Result')"
                        :key="`${index}_result`"
                        :width="85"
                        :hiding-priority="5000-(index*2)"
                        cell-template="resultTemplate"
                        :name="wave">
            </dx-column>
            <dx-column  :data-field="getWaveNumShopsField(index+1)"
                        alignment="left"
                        :visible="true"
                        caption="n"
                        :key="`${index}_shops`"
                        :customizeText="getWaveNumOfShopsText"
                        :width="65"
                        :hiding-priority="4999-(index*2)"
                        :name="wave + '-n'">
            </dx-column>
          </dx-column>
        </template>

        <dx-column :caption="$t('DealerReport.WaveCompare')"> <!-- :allowExporting="false">-->
            <dx-column  id="dxdatagridtotalcol"
                        ref="dxdatagridtotalcol"
                        :allow-sorting="false"
                        data-field="SurveyWave"
                        :caption="$t('Webportal.Dashboard.OverallByQuestion')"
                        cell-template="chartTemplate"
                        :hiding-priority="0"
                        name="##IGNORE##">
                        <!--:allowExporting="false">-->
            </dx-column>
        </dx-column>

        <dx-column :caption="$t('Webportal.Dashboard.Total')">
            <dx-column  :data-field="getWaveResultField(0)"
                        :visible="true"
                        :caption="$t('Webportal.Result')"
                        cell-template="resultTemplate"
                        :width="85"
                        :hiding-priority="9999"
                        :name="$t('Webportal.Dashboard.Total')">
            </dx-column>
            <dx-column  :data-field="getWaveNumShopsField(0)"
                        alignment="left"
                        :visible="true"
                        caption="N"
                        :customizeText="getWaveNumOfShopsText"
                        :width="65"
                        :hiding-priority="1"
                        name="N">
            </dx-column>
        </dx-column>

        <dx-summary :calculate-custom-summary="calculateRow">

          <!-- For each wave -->
          <template v-for="(wave, index) in this.waves" >
            <dx-group-item
              :name="`resultSummary_${wave}_${index+1}`"
              :show-in-group-footer="false"
              :align-by-column="true"
              :showInColumn="getWaveResultField(index+1)"
              summary-type="custom"
              :key="`${index+1}_resultSummary`"
              :customizeText="getWaveResultText"
            />
            <dx-group-item
              :name="`numOfShopsSummary_${wave}_${index+1}`"
              :show-in-group-footer="false"
              :align-by-column="true"
              :showInColumn="getWaveNumShopsField(index+1)"
              summary-type="custom"
              :key="`${index+1}_shopsSummary`"
            />
          </template>

          <!-- The total -->
          <dx-group-item
            name="resultSummary_Total_0"
            :show-in-group-footer="false"
            :align-by-column="true"
            :showInColumn="getWaveResultField(0)"
            summary-type="custom"
            :customizeText="getWaveResultText"
          />
          <dx-group-item
            name="numOfShopsSummary_Total_0"
            :show-in-group-footer="false"
            :align-by-column="true"
            :showInColumn="getWaveNumShopsField(0)"
            summary-type="custom"
          />
        </dx-summary>

        <dx-group-panel :visible="false"/>

        <dx-search-panel
          :visible="true"
          :highlight-case-sensitive="true"/>

        <div slot="collapseall" slot-scope="data">
          <dx-check-box
            v-model="collapseAllValue"
            :text="$t('Webportal.Dashboard.ExpandCollapse')"
            :onValueChanged="onCollapseAllChanged" />
        </div>

        <div slot="groupmode" slot-scope="data">
          <div class="flex-container group-switch space-left">
            <div for="GroupSwitch">
              {{ $t('Webportal.Dashboard.GroupMode') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Dashboard.GroupMode.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </div>
            <label class="switch switch-label switch-secondary space-left">
              <input class="switch-input" type="checkbox"
                    v-model="groupModeEnabled"
                    v-validate data-vv-name="GroupSwitch"
                    :disabled="!compareModeEnabled">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <div slot="download" slot-scope="data">
          <div class="form-row">
            <div class="col-md-auto">
              <b-alert :show="noDataAlert"
                      dismissible
                      @dismissed="noDataAlert=0"
                      variant="warning">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>
              <b-button class="excelButton"
                        variant="primary"
                        :disabled="isExporting || !dashdata"
                        @click="downloadExcel()"
                        size="sm"
                        v-b-tooltip.hover 
                        :title="$t('Webportal.Download')">
                <font-awesome-icon icon="file-excel" />
              </b-button>
            </div>
            <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
          </div>
        </div>

        <result-cell slot="resultTemplate" slot-scope="options" :cell-data="options" :thresholds="thresholds" :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints" />
        <chart-cell slot="chartTemplate" slot-scope="options" 
          :cell-data="options" 
          :thresholds="thresholds" 
          :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints" 
          :groupData="getGroupData(options)" />
        <ClusterChart slot="clusterChartTemplate" slot-scope="options" :cell-data="options" :dataSource="testData"/>
        <summary-cell slot="summaryCellTemplate" slot-scope="options" :cell-data="options" />

      </dx-data-grid>
    </div>
  </div>
</template>

<script>

import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxTexts,
  DxSummary,
  DxSorting,
  DxGroupItem,
  DxValueFormat
} from 'devextreme-vue/data-grid';

import { DxCheckBox } from 'devextreme-vue/check-box';

// import WaveCell from './WaveCell.vue';
import ChartCell from './ChartCell.vue';
import ClusterChart from './ClusterChart.vue'
import ResultCell from './ResultCell.vue';
import SummaryCell from './SummaryCell.vue';
import scale from './Scale';
import foot from './Foot.vue';
import ThresholdColor from '@/mixins/ThresholdColor';

export default {
  name: 'ResultTable',

  components: {
    DxDataGrid,
    DxExport,
    DxColumn,
    DxGrouping,
    DxGroupPanel,
    DxPager,
    DxPaging,
    DxSearchPanel,
    DxTexts,
    DxSummary,
    DxSorting,
    DxGroupItem,
    DxValueFormat,
    DxCheckBox,
    ChartCell,
    ClusterChart,
    ResultCell,
    SummaryCell,
    scale,
    foot
  },
  
  mixins: [ ThresholdColor ],
  
  props: {
    dashdata: { type: Object, default: null },
    isCluster: {default: true},
    thresholds: null,
    percentageNumberOfDecimalPoints: {type: Number, default: 2},
    activeCompareLabel: {type: String, default: 'N/A'},
    componentId: {type: Number, default: 0}
  },

  watch: {
    dashdata: {
      immediate: true,
      handler(newVal, oldVal) {
        // console.log('ResultTable - watch - dashdata: ' + JSON.stringify(newVal));
        this.dashdata = newVal;
        this.handleDataChanged();
        this.componentKey +=1;
      },
    },

    groupModeEnabled: {
      immediate: true,
      handler(newVal, oldVal) {
        this.handleDataChanged();
        this.componentKey +=1;
      },
    },

    activeCompareLabel: {
      immediate: true,
      handler(newVal, oldVal) {
        this.handleDataChanged();
        this.componentKey +=1;
      },
    },

    componentId: {
      immediate: true,
      handler(newVal, oldVal) {
        this.handleDataChanged();
        this.componentKey +=1;
      },
    },
  },

  created() {
    // console.log('ResultTable - created - data: ' + JSON.stringify(this.dashdata));
    this.handleDataChanged();
  },

  mounted() {
    // console.log('ResultTable - mounted');
    this.intervalReference = setInterval(this.manipulateGridDom, 100);
  },

  data() {
    return {
      waves: [],
      smallscreenactive: false,
      groups: [],
      componentKey: 0,
      groupList: [],
      groupList2: [],
      intervalReference: undefined,
      domManipulationRetryCounter: 0,
      smallScreenSwitchWidth: 120,
      collapseAllValue: false,
      compareModeEnabled: false,
      groupModeEnabled: true,
      chartCellWidth: 250,
      isExporting: false,
      noDataAlert: 0,

      toolTipDelay: { delay: { "show": 500, "hide": 100 } },
      // [34.12,78.33,55.55,99.99]
      testData: [{
          arg: 'Welle1',
          val: 34
        }, {
          arg: 'Welle2',
          val: 78
        }, {
          arg: 'Welle3',
          val: 55
        }, {
          arg: 'Welle4',
          val: 99
        }],
    };
  },

  methods: {

    onCollapseAllChanged(event) {
      const grid = this.$refs['dxdatagrid'];

      if (grid) {
        if (event.value) {
          grid.$_instance.expandAll(0);
        } else if (event.value !== undefined) {
          grid.$_instance.collapseAll(0);
        }
      }
    },

    onGridRowCollapsed(event) {
      this.checkRowCollapsedValue();
    },

    onGridRowExpanded(event) {
      this.checkRowCollapsedValue();
    },

    onGridAdaptiveDetailRowPreparing(event) {
      if (event.formOptions && event.formOptions.items) {

        let indexesToRemove = [];

        for (let i=0; i<event.formOptions.items.length; i++) {
          const item = event.formOptions.items[i];
          if (item.column && item.name) {
            if (item.name === '##IGNORE##')
            {
              indexesToRemove.push(i);
            } else {
              item.column.caption = item.name;
            }
          }
        }

        if (indexesToRemove.length > 0) {
          for (let i=indexesToRemove.length-1; i>=0; i--) {
            event.formOptions.items.splice(indexesToRemove[i], 1);
          }
        }
      }
    },

    checkRowCollapsedValue() {
      let state = undefined;
      const grid = this.$refs['dxdatagrid'];

      if (grid) {
        for (let row of grid.$_instance.getVisibleRows())
        {
          if (row.rowType === 'group') {
            if (state === undefined) {
              state = row.isExpanded;
            } else {
              if (state !== row.isExpanded) {
                // not all rows are expanded/collapsed => mixed
                state = undefined;
                break;
              }
            }
          }        
        }
      }

      this.collapseAllValue = state;
    },

    onToolbarPreparing(tool) {
      tool.toolbarOptions.items.unshift({
        location: 'before',
        template: 'groupmode'
      });

      tool.toolbarOptions.items.unshift({
        location: 'before',
        template: 'collapseall'
      });

      tool.toolbarOptions.items.unshift({
        location: "after",
        template: "download"
      });
    },

    manipulateGridDom() {

      // prevent endless interval calls
      if (this.domManipulationRetryCounter === 30) {
        clearInterval(this.intervalReference);
        return;
      }

      // try to get the group item elements
      var htmlElements = this.$el.getElementsByClassName('dx-datagrid-summary-item');

      if (htmlElements && htmlElements.length > 0) {
        clearInterval(this.intervalReference);

        for (let rsElement of htmlElements) {
          const resultVal = rsElement.innerText;
          // there are result values and num of shops elements
          // we only want to do s.th. for teh result values
          if (resultVal.endsWith('%')) {

            // check if previous call already added the element(s)
            var prevElement = rsElement.previousElementSibling;
            if (prevElement && prevElement.attributes.length > 0 && prevElement.attributes[0].value === 'thresholdIndicator') {
              continue;
            }

            // enable flex row for this cell
            var pElement = rsElement.parentElement;
            // var attr = document.createAttribute("style");
            // attr.value = 'display: flex; flex-direction: row; text-align: left;'
            // pElement.setAttributeNode(attr);

            // add indicator element
            var indicatorElement = document.createElement("div");
            var attr = document.createAttribute("id");
            attr.value = 'thresholdIndicator';
            indicatorElement.setAttributeNode(attr);
            attr = document.createAttribute("style");
            attr.value = 'background-color: ' + this.getThresholdColor(this.thresholds, resultVal, '#ff201c') + '; margin-top: 2px; margin-right: 5px; width: 15px; height: 15px; float: left;';
            indicatorElement.setAttributeNode(attr);

            pElement.insertBefore(indicatorElement, rsElement);
          }
        }
      }

      this.checkRowCollapsedValue();
    },

    handleDataChanged() {

      this.groupList = [];
      this.groupList2 = [];
      
      // get wave count
      if (this.dashdata && this.dashdata.data[0] && this.dashdata.data[0].OverallByWave && this.dashdata.data[0].OverallByWave[0].SurveyWave && this.dashdata.data[0].OverallByWave[0].SurveyWave.length > 0) {
        
        let newWaves = [];
        for (let wave of this.dashdata.data[0].OverallByWave[0].SurveyWave) {
          newWaves.push(wave.Name);
        }
        this.waves = newWaves;
        //console.log("handleDataChanged - waves: " + JSON.stringify(this.waves));

        // get all KPI Cluster
        for (let item of this.dashdata.list) {

          // groupList
          if (this.isCluster) {
            if (!this.groupList.includes(item.Code)) {
              this.groupList.push(item.Code);
            }
          } else {
            if (!this.groupList.includes(item.KpiGroupCode)) {
              this.groupList.push(item.KpiGroupCode);
            }
          }

          // groupList2
          if (!this.groupList2.includes(item.GroupCode)) {
            this.groupList2.push(item.GroupCode);
          }
        }
        //console.log("handleDataChanged - groupList: " + JSON.stringify(this.groupList));
        
        // we have to add additional waves for all cluster and kpi values,
        // because datagrid binding need exactly all waves in all data items
        // if you have two different surveys selected it's possible that
        // clusters or kpis does'nt have all waves included
        for (let dataItem of this.dashdata.list) {        
          if (dataItem.SurveyWave && dataItem.SurveyWave.length > 0) {

            // if length of contained waves are different of expected, fill it with dummies
            // the data will always contains the 'total' wave, which we have to ignore here
            if (dataItem.SurveyWave.length != (this.waves.length + 1)) {

              let newWaveArray = [];
              let oldWaveArray = [...dataItem.SurveyWave];

              // add total wave
              newWaveArray.push(oldWaveArray[0]);

              // add each wave
              for (let index=0; index < this.waves.length; index++) {

                const currWaveName = this.waves[index];
                let waveData = oldWaveArray.find(w => w.Name === currWaveName);

                // if the wave data is found we add it otherwise wwe have to add a dummy
                if (waveData != null || waveData != undefined) {
                  newWaveArray.push(waveData);
                } else {
                  newWaveArray.push({
                    Id: undefined,
                    Name: currWaveName,
                    Result: undefined,
                    PointsReached: undefined,
                    PointsMax: undefined,
                    CheckCountTotal: undefined
                  });
                }
              }

              // replace the wave data
              dataItem.SurveyWave = newWaveArray;
            }
          }
        }
      }

      // determine group mode
      this.compareModeEnabled = this.dashdata ? this.dashdata.isGroupMode : false;
      this.checkRowCollapsedValue();
    },

    getWaveName(index) {

      // console.log('getWaveName with requested index: ' + index);
      // console.log(this.dashdata.list[0]);

      // usually we have to reduce the index, because
      // the v-for loop will return index based on 1
      // but we also have to ignore the total wave
      if (this.dashdata && this.dashdata.list && this.dashdata.list.length > 0 && this.dashdata.list[0].SurveyWave && this.dashdata.list[0].SurveyWave.length > index) {
        return this.dashdata.list[0].SurveyWave[index].Name;
      } else {
        return "Wave";
      }
    },

    getSurveyWave(index) {

      return "SurveyWave";
    },

    onRowClick: function(e) {
      // console.log('ResultTable - onRowClick');

      if (e['groupIndex'] !== undefined && e['isExpanded'] !== undefined) {

        if (e.isExpanded) {
          e.component.collapseRow(e.key);
        } else {
          e.component.expandRow(e.key);
        }
      }
    },

    onCellPrepared: function(e){
      // console.log('ResultTable - onCellPrepared');

      if(e.rowType == 'header') {
        e.cellElement.style.fontWeight = 'bold';
        e.cellElement.style.color = '#EEE';
        e.cellElement.style.backgroundColor = '#737178';
      } else {
        e.cellElement.style.backgroundColor = '#f7f7f7';
      }
    },

    getGroupColName: function() {
      return this.isCluster ? 'Code' : 'KpiGroupCode';
    },

    getClusterCaption: function() {
      return this.isCluster ? this.$t('Webportal.Cluster') : this.$t('Webportal.Kpi');
    },

    getSubClusterCaption: function() {
      return this.isCluster ? this.$t('Webportal.ClusterQuestion') : this.$t('Webportal.Kpi');
    },

    getWaveResultField: function(index) {
      // console.log('getWaveResultField: ' + index);
      return 'SurveyWave[' + index + '].Result';
    },

    getWaveNumShopsField: function(index) {
      return 'SurveyWave[' + index + '].CheckCountTotal';
    },

    getWaveResultText: function(text) {
      // console.log('getWaveResultText: ' + text);
      if (text === undefined || text.value === undefined) {
        return undefined;
      }

      let retVal = 'N/A';
      const val = text.value ? Number(text.value.replace(',','.')) : Number(text);
      
      if (!Number.isNaN(val)) {
        retVal = (val == 100) ? 
          this.$n(val, { minimumFractionDigits: 0, maximumFractionDigits: 0}) + '%': 
          this.$n(val, { minimumFractionDigits: this.percentageNumberOfDecimalPoints, maximumFractionDigits: this.percentageNumberOfDecimalPoints}) + '%';
      }

      return retVal;
    },

    getWaveNumOfShopsText: function(text) {
      //console.log('getWaveNumOfShopsText: ' + text);
      let val = text.value ? text.value : text;
      if (val.valueText === '') {
        val = null;
      }
      return (val === null || val === undefined || val === '') ? 'N/A' : val.toString();
    },

    getOverallResultField: function() {
      return 'dashdata.data[0].Overall.Result';
    },

    downloadExcel() {
      const grid = this.$refs['dxdatagrid'];

      if (grid) {
        // console.log(grid);
        grid.$_instance.exportToExcel(false);
      }
    },

    customizeExcelCell(options) {

      if(options.gridCell && options.gridCell.rowType === 'data' || options.gridCell && options.gridCell.rowType === 'group') {

        if (options.gridCell.column.dataField === 'SurveyWave') {
          options.value = '';
        } else if (options.value && options.gridCell.column.dataField.startsWith('SurveyWave') && options.gridCell.column.dataField.endsWith('Result')) {
          // console.log('customizeExcelCell - Field: ' + options.gridCell.column.dataField + ' - value: ' + options.value);
          options.value = options.value.replace('%', '');
          options.backgroundColor = this.getThresholdColor(Number(options.value));
        } else if (options.value && options.gridCell.column.dataField === 'GroupCode') {
          if (options.value.indexOf('^') >= 0) {
            options.value = options.value.substring(options.value.indexOf('^') + 1);
          }
        } else if (options.value && options.gridCell.column.dataField === this.getGroupColName()) {
          if (options.value.indexOf(':') >= 0) {
            options.value = options.value.substring(options.value.indexOf(':') + 1);
          }
        }
      }
    },

    calculateRow(options) {
      //console.log("calculateRow - name: " + options.name + " - summaryProcess: " + options.summaryProcess);
      let waveName = this.getSummaryWaveName(options.name);

      if (options.summaryProcess == 'calculate') {
        if (options.totalValue)
          return;

        let dataSource = (options.groupIndex === 0) ? this.dashdata.data[0] : options.value.DataSource;
        let groupData = this.isCluster ? dataSource.Cluster : dataSource.KPIGroup;
        let item = groupData.find(obj => obj.Code === options.value.KpiCodeParent);
        let wave = item ? item.SurveyWave.find(obj => obj.Name === waveName) : null;

        if (options.name.startsWith('resultSummary') && wave)
          options.totalValue = wave.Result;
        else if (options.name.startsWith('numOfShopsSummary') && wave && wave.CheckCountTotal)
          options.totalValue = this.getWaveNumOfShopsText(wave.CheckCountTotal);
        else
          options.totalValue = 'N/A';
      }
    },
    
    getSummaryWaveName(name) {
      const indexOfLast = name.lastIndexOf('_');
      const indexOfFirst = name.indexOf('_');
      const retVal = name.substring(indexOfFirst+1,indexOfLast);
      return retVal;
    },
    
    getSummaryIndexOutOfName(name) {
      const indexStr = name.substring(name.lastIndexOf('_') + 1);
      return Number(indexStr);
    },

    getExcelFilename() {
      const now = new Date();
      return 'AnalysisGridData_' + now.getFullYear() + '_' + (now.getMonth()+1) + '_' + now.getDate() + '_' + now.getHours() + '_' + now.getMinutes() + '_' + now.getSeconds();
    },

    getWidthToBar() {
      // 7 -> column padding
      // 1 -> border
      // 30 -> Group arrow (for each level)
      // 100 -> Compare column
      // 260 -> Cluster/KPI column
      // Each wave will take 150px width (Total 'Wave' is at the end)
      let retVal = '0px';

      if (this.dashdata && this.dashdata.list && this.dashdata.list.length > 0) {

        let width = (7 + 1 + 30 + 260 + Math.max(this.dashdata.list[0].SurveyWave.length - 1, 0) * 150);
        // add additional 30px, if group mode is enabled
        if (this.compareModeEnabled) {
          if (this.groupModeEnabled) {
            width += 30; // Group arrow
          } else {
            width += 100; // Compare column
          }
        }

        retVal = width + 'px';
      }
      return retVal;
    },

    getGroupName(cellInfo) {
      if (this.isCluster) {
        let clusterItem = this.dashdata.data[0].Cluster.find(obj => obj.Code === cellInfo.value);
        return clusterItem != null ? clusterItem.Name : cellInfo.value;
      } else {
        let kpiGroupItem = this.dashdata.data[0].KPIGroup.find(obj => obj.Code === cellInfo.value);
        return kpiGroupItem != null ? kpiGroupItem.Name : cellInfo.value;
      }
    },

    getGroupData(options) {

      let retVal = [];
      const dataIndex = this.getDataIndex(options);

      if (this.isCluster) {
          if (this.dashdata && this.dashdata.data[dataIndex].Cluster) {
            retVal = this.dashdata.data[dataIndex].Cluster;
          }
      } else {
          if (this.dashdata && this.dashdata.data[dataIndex].KPIGroup) {
            retVal = this.dashdata.data[dataIndex].KPIGroup;
          }
      }

      return retVal;
    },

    getDataIndex(options) {
      let retVal = 0;

      // if groupIndex = 1 (subgroup) then the data index is rowIndex - 1
      // in all other cases (main group or fallback) data index = 0
      if (options && options.rowType === "group" && options.row.groupIndex === 1){
        retVal = this.groupList2.findIndex(obj => obj === options.row.key[1]) + 1;
        retVal = Math.min(retVal, this.dashdata.data.length - 1);
        // console.log('Index determined: ' + retVal);
      }

      return retVal;
    }
  },
};

</script>

<style scoped>

.excelButton {
  background-color: #107c41;
  border-color: #107c41;
}

.flex-container {
  display: flex;
  align-items: center;
}

.space-left {
  margin-left: 5px;
}

.group-switch {
  margin-bottom: 1px;
  margin-right: 5px;
  color: #333;
  font-weight: normal;
  font-size: 14px;
  font-family: 'Helvetica Neue', 'Segoe UI', Helvetica, Verdana, sans-serif;
}

.card-body {
  padding: 0px;  margin-bottom: 0.5rem;
}

.dx-fieldset {
  margin: 1rem;
}

#tasks {
    min-height: 150px;
}

#tasks .dx-treelist-rowsview td {
    vertical-align: middle;
}

.pitem {
  text-align: center;
  height: 40px;
  /* width: 300px; */
  margin-left: 347px;
  margin-right: 228px;
}
</style>
